import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  TextField,
  InputAdornment,
  Grid,
  IconButton,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Pagination,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
} from "@mui/material";
import {
  Add,
  Search,
  MoreVert,
  Receipt,
  Visibility,
  CheckCircle,
  Cancel,
  Warning,
  Restore,
  Delete,
} from "@mui/icons-material";
import {itemIssueReturnService} from "../../services/itemIssueReturnService";
import { useSnackbar } from "notistack";

export const ItemIssueReturnList = () => {
  const [itemIssueReturns, setItemIssueReturns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedReturn, setSelectedReturn] = useState(null);
  const [approveDialog, setApproveDialog] = useState(false);
  const [rejectDialog, setRejectDialog] = useState(false);
  const [completeDialog, setCompleteDialog] = useState(false);
  const [rejectionReason, setRejectionReason] = useState("");

  // Pagination state
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    pages: 0,
  });

  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();

  // Debounced search
  useEffect(() => {
    const timer = setTimeout(() => {
      fetchItemIssueReturns();
    }, 500);

    return () => clearTimeout(timer);
  }, [pagination.page, pagination.limit, searchTerm, statusFilter]);

  const fetchItemIssueReturns = async () => {
    try {
      setLoading(true);
      const params = {
        page: pagination.page,
        limit: pagination.limit,
        ...(searchTerm && { search: searchTerm }),
        ...(statusFilter && { status: statusFilter }),
      };

      const response = await itemIssueReturnService.getItemIssueReturns(params);
      const { itemIssueReturns: returnsData, pagination: paginationData } =
        response.data.data;

      setItemIssueReturns(returnsData || []);
      setPagination((prev) => ({
        ...prev,
        total: paginationData?.total || 0,
        pages: paginationData?.pages || 0,
      }));
    } catch (error) {
      enqueueSnackbar("Error fetching item issue returns", { variant: "error" });
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (value) => {
    setSearchTerm(value);
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  const handleStatusFilterChange = (value) => {
    setStatusFilter(value);
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  const handlePageChange = (event, value) => {
    setPagination((prev) => ({ ...prev, page: value }));
  };

  const handleMenuOpen = (event, returnItem) => {
    setAnchorEl(event.currentTarget);
    setSelectedReturn(returnItem);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedReturn(null);
  };

  const handleApprove = async () => {
    try {
      await itemIssueReturnService.approveItemIssueReturn(selectedReturn._id, {});
      enqueueSnackbar("Item issue return approved successfully", { variant: "success" });
      setApproveDialog(false);
      fetchItemIssueReturns();
    } catch (error) {
      enqueueSnackbar(
        error.response?.data?.message || "Error approving item issue return",
        { variant: "error" }
      );
    }
    handleMenuClose();
  };

  const handleReject = async () => {
    if (!rejectionReason.trim()) {
      enqueueSnackbar("Please provide a rejection reason", {
        variant: "warning",
      });
      return;
    }

    try {
      await itemIssueReturnService.rejectItemIssueReturn(selectedReturn._id, { rejectionReason });
      enqueueSnackbar("Item issue return rejected successfully", { variant: "success" });
      setRejectDialog(false);
      setRejectionReason("");
      fetchItemIssueReturns();
    } catch (error) {
      enqueueSnackbar(
        error.response?.data?.message || "Error rejecting item issue return",
        { variant: "error" }
      );
    }
    handleMenuClose();
  };

  const handleComplete = async () => {
    
    try {
      await itemIssueReturnService.completeItemIssueReturn(selectedReturn._id);
      enqueueSnackbar("Item issue return completed successfully", { variant: "success" });
      setCompleteDialog(false);
      fetchItemIssueReturns();
    } catch (error) {
      
      enqueueSnackbar(
        error.response?.data?.message || "Error completing item issue return",
        { variant: "error" }
      );
    }
    handleMenuClose();
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: "warning",
      approved: "info",
      completed: "success",
      rejected: "error",
      cancelled: "default",
    };
    return colors[status] || "default";
  };

  const getStatusIcon = (status) => {
    const icons = {
      pending: <Warning />,
      approved: <CheckCircle />,
      completed: <CheckCircle />,
      rejected: <Cancel />,
      cancelled: <Cancel />,
    };
    return icons[status];
  };

  return (
    <Box  
      sx={{
        minHeight:'100vh',
        background: 'linear-gradient(180deg, #f9fafc 0%, #f2f5f8 100%)',
      }}
    >
    <Box>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 4,
        }}
      >
        <Typography variant="h4" fontWeight="bold">
          Item Issue Returns Management
        </Typography>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={() => navigate("/hospital/hospital-inventory/item-issue-returns/new")}
        >
          New Return
        </Button>
      </Box>

      {/* Search and Filter Card */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                variant="outlined"
                placeholder="Search by return number, department name, or issue number..."
                value={searchTerm}
                onChange={(e) => handleSearch(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth>
                <InputLabel>Status Filter</InputLabel>
                <Select
                  value={statusFilter}
                  label="Status Filter"
                  onChange={(e) => handleStatusFilterChange(e.target.value)}
                >
                  <MenuItem value="">All Statuses</MenuItem>
                  <MenuItem value="pending">Pending</MenuItem>
                  <MenuItem value="approved">Approved</MenuItem>
                  <MenuItem value="completed">Completed</MenuItem>
                  <MenuItem value="rejected">Rejected</MenuItem>
                  <MenuItem value="cancelled">Cancelled</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={3}>
              <Typography variant="body2" color="text.secondary" align="center">
                Total: {pagination.total} returns
              </Typography>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Returns Summary */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent sx={{ textAlign: "center" }}>
              <Typography variant="h4" fontWeight="bold" color="warning.main">
                {itemIssueReturns?.filter((r) => r.status === "pending").length}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Pending Approval
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent sx={{ textAlign: "center" }}>
              <Typography variant="h4" fontWeight="bold" color="info.main">
                {itemIssueReturns?.filter((r) => r.status === "approved").length}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Approved
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent sx={{ textAlign: "center" }}>
              <Typography variant="h4" fontWeight="bold" color="success.main">
                {itemIssueReturns?.filter((r) => r.status === "completed").length}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Completed
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent sx={{ textAlign: "center" }}>
              <Typography variant="h4" fontWeight="bold" color="error.main">
                {itemIssueReturns?.filter((r) => r.status === "rejected").length}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Rejected
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Returns Table */}
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom fontWeight="bold">
            Recent Item Issue Returns
          </Typography>

          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              <TableContainer component={Paper} variant="outlined">
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Return #</TableCell>
                      <TableCell>Issue #</TableCell>
                      <TableCell>Department</TableCell>
                      <TableCell>Date</TableCell>
                      <TableCell align="center">Restockable</TableCell>
                      <TableCell align="center">Disposal Required</TableCell>
                      <TableCell align="center">Status</TableCell>
                      <TableCell align="center">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {itemIssueReturns?.map((returnItem) => (
                      <TableRow key={returnItem._id} hover>
                        <TableCell>
                          <Typography variant="subtitle2" fontWeight="bold">
                            {returnItem.returnNumber}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {returnItem.itemIssue?.issueNumber || "N/A"}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          {returnItem.department ? (
                            <>
                              <Typography variant="body2">
                                {returnItem.department.department}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {returnItem.department.code}
                              </Typography>
                            </>
                          ) : (
                            <Typography variant="body2" color="text.secondary">
                              No Department
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {new Date(
                              returnItem.createdAt
                            ).toLocaleDateString()}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          {returnItem.items?.some((item) => item.restockable) ? (
                            <Chip
                              icon={<Restore />}
                              label="Yes"
                              color="success"
                              size="small"
                              variant="outlined"
                            />
                          ) : (
                            <Chip
                              label="No"
                              color="default"
                              size="small"
                              variant="outlined"
                            />
                          )}
                        </TableCell>
                        <TableCell align="center">
                          {returnItem.items?.some((item) => item.disposalRequired) ? (
                            <Chip
                              icon={<Delete />}
                              label="Yes"
                              color="error"
                              size="small"
                              variant="outlined"
                            />
                          ) : (
                            <Chip
                              label="No"
                              color="default"
                              size="small"
                              variant="outlined"
                            />
                          )}
                        </TableCell>
                        <TableCell align="center">
                          <Chip
                            icon={getStatusIcon(returnItem.status)}
                            label={returnItem.status.toUpperCase()}
                            color={getStatusColor(returnItem.status)}
                            size="small"
                          />
                        </TableCell>
                        <TableCell align="center">
                          <IconButton
                            size="small"
                            onClick={(e) => handleMenuOpen(e, returnItem)}
                          >
                            <MoreVert />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              {itemIssueReturns?.length === 0 && (
                <Box sx={{ textAlign: "center", py: 4 }}>
                  <Receipt
                    sx={{ fontSize: 64, color: "text.secondary", mb: 2 }}
                  />
                  <Typography variant="h6" color="text.secondary" gutterBottom>
                    No item issue returns found
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mb: 3 }}
                  >
                    {searchTerm || statusFilter
                      ? "Try adjusting your search or filter criteria"
                      : "Get started by creating your first return"}
                  </Typography>
                  <Button
                    variant="contained"
                    startIcon={<Add />}
                    onClick={() => navigate("/hospital/hospital-inventory/item-issue-returns/new")}
                  >
                    Create First Return
                  </Button>
                </Box>
              )}

              {/* Pagination */}
              {pagination.pages > 1 && (
                <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
                  <Pagination
                    count={pagination.pages}
                    page={pagination.page}
                    onChange={handlePageChange}
                    color="primary"
                    showFirstButton
                    showLastButton
                  />
                </Box>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* Action Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={() => {
          navigate(`/hospital/hospital-inventory/item-issue-returns/${selectedReturn?._id}`);
          handleMenuClose();
        }}>
          <Visibility sx={{ mr: 1 }} />
          View Details
        </MenuItem>

        {selectedReturn?.status === "pending" && [
          <MenuItem key="approve" onClick={() => {
            setApproveDialog(true);
           
          }}>
            <CheckCircle sx={{ mr: 1 }} color="success" />
            Approve Return
          </MenuItem>,
          <MenuItem key="reject" onClick={() => {
            setRejectDialog(true);
            
          }}>
            <Cancel sx={{ mr: 1 }} color="error" />
            Reject Return
          </MenuItem>,
        ]}

        {selectedReturn?.status === "approved" && (
          <MenuItem onClick={() => {
            setCompleteDialog(true);
           
            
          }}>
            <CheckCircle sx={{ mr: 1 }} color="success" />
            Complete Return
          </MenuItem>
        )}
      </Menu>

      {/* Approve Dialog */}
      <Dialog open={approveDialog} onClose={() => setApproveDialog(false)}>
        <DialogTitle>Approve Item Issue Return</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            Are you sure you want to approve this return? This will prepare items for restocking or disposal.
          </Alert>
          <Typography variant="body2">
            Return: <strong>{selectedReturn?.returnNumber}</strong>
          </Typography>
          {selectedReturn?.items?.some((item) => item.restockable) && (
            <Typography variant="body2" color="success.main">
              Some items will be restocked
            </Typography>
          )}
          {selectedReturn?.items?.some((item) => item.disposalRequired) && (
            <Typography variant="body2" color="error.main">
              Some items require disposal
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setApproveDialog(false)}>Cancel</Button>
          <Button variant="contained" color="success" onClick={handleApprove}>
            Approve
          </Button>
        </DialogActions>
      </Dialog>

      {/* Reject Dialog */}
      <Dialog open={rejectDialog} onClose={() => setRejectDialog(false)}>
        <DialogTitle>Reject Item Issue Return</DialogTitle>
        <DialogContent>
          <Alert severity="error" sx={{ mb: 2 }}>
            Please provide a reason for rejecting this return.
          </Alert>
          <TextField
            fullWidth
            multiline
            rows={3}
            label="Rejection Reason"
            value={rejectionReason}
            onChange={(e) => setRejectionReason(e.target.value)}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRejectDialog(false)}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleReject}>
            Reject
          </Button>
        </DialogActions>
      </Dialog>

      {/* Complete Dialog */}
      <Dialog open={completeDialog} onClose={() => setCompleteDialog(false)}>
        <DialogTitle>Complete Item Issue Return</DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 2 }}>
            Are you sure you want to complete this return? Restockable items will be returned to inventory.
          </Alert>
          <Typography variant="body2">
            Return: <strong>{selectedReturn?.returnNumber}</strong>
          </Typography>
          {selectedReturn?.items?.some((item) => item.restockable) && (
            <Typography variant="body2" color="success.main" sx={{ mt: 1 }}>
              ✓ Restockable items will be added back to inventory
            </Typography>
          )}
          {selectedReturn?.items?.some((item) => item.disposalRequired) && (
            <Typography variant="body2" color="error.main" sx={{ mt: 1 }}>
              ⚠ Items marked for disposal will not be restocked
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCompleteDialog(false)}>Cancel</Button>
          <Button variant="contained" color="primary" onClick={handleComplete}>
            Complete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
    </Box>
  );
};
